import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Sidebar } from 'primereact/sidebar';
import { useEffect, useRef, useState } from 'react';
import FormInput from '../../../../components/Form/FormInput';
import RichTextEditor from '../../../../components/Form/RichTextEditor';
import LanguageManagerGuard from '../../../../components/Language/LanguageManagerGuard';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { privateFetch } from '../../../../utils/apiHelper';
import ImageForm from '../../common/image/ImageForm';

function ProjectPanel({ isOpen, onClose, project, loading, onUpdate, onCreate }) {
  const [title, setTitle] = useState('');
  const [year, setYear] = useState(0);
  const [isHighlight, setIsHighlight] = useState(false);
  const [description, setDescription] = useState('');
  const [review, setReview] = useState('');
  const [mainImage, setMainImage] = useState(undefined);
  const [surface, setSurface] = useState(null);
  const autocompleteRef = useRef(null);
  const [placeList, setPlaceList] = useState([]);
  const [placeQuery, setPlaceQuery] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(undefined);

  const { accountContext } = useAccountContext();

  useEffect(() => {
    if (project) {
      setTitle(project.title);
      setYear(project.year);
      setIsHighlight(project.is_highlight);
      setDescription(project.description);
      setReview(project.review);
      setMainImage(project.main_image);
      setPlaceQuery(`${project.localisation.city}, ${project.localisation.country}`);
      setSelectedPlace(project.localisation);
      setSurface(project.surface);
    } else {
      setTitle('');
      setYear(0);
      setIsHighlight(false);
      setDescription('');
      setMainImage(undefined);
      setSurface(null);
      setPlaceQuery('');
      setReview('');
      setSelectedPlace(undefined);
    }
  }, [project, isOpen]);

  const fetchPlaceList = async () => {
    await privateFetch(
      'GET',
      'resources',
      `/cu/${accountContext.id}/address/autocomplete/?query=${placeQuery}`
    ).then((res) => {
      setPlaceList(res);
    });
  };

  const handleValidate = () => {
    const projectPayload = {
      title,
      year,
      is_highlight: isHighlight,
      description,
      place_id: selectedPlace.place_id,
      surface,
      main_image: mainImage?.id,
      review
    };
    project ? onUpdate(projectPayload) : onCreate(projectPayload);
  };

  const formIsValid = () => {
    return (
      title &&
      mainImage &&
      year &&
      selectedPlace &&
      (isHighlight ? surface && description.length : true)
    );
  };

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={`Valider la ${project ? 'modification' : 'création'}`}
        className="w-full"
        onClick={handleValidate}
        severity="success"
        disabled={!formIsValid()}
        loading={loading}
      />
    </div>
  );

  const addressTemplate = (item) => {
    return <div>{`${item.name}`}</div>;
  };

  return (
    <Sidebar
      style={{ width: 700 }}
      visible={isOpen}
      position="right"
      onHide={() => onClose(false)}
      header={
        <h3 className="text-slate-800 text-2xl font-medium">{`${project ? 'Modifier' : 'Ajouter'} un projet`}</h3>
      }>
      <LanguageManagerGuard isCreation={!project}>
        <div className="sidebar-content-container">
          <div className="form-wrapper !pb-60">
            <FormInput label={'Titre du projet'} required isValid={title.length} translatable>
              <InputText
                id="title"
                value={title}
                maxLength={300}
                placeholder="Veuillez saisir le titre du projet"
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormInput>
            <FormInput label={'Année'} required isValid={year}>
              <InputNumber
                id="year"
                value={year}
                placeholder="Veuillez saisir l'année"
                onChange={(e) => setYear(e.value)}
                locale="fr"
              />
            </FormInput>

            <FormInput label={'Lieu'} required isValid={selectedPlace}>
              <AutoComplete
                ref={autocompleteRef}
                field="name"
                inputClassName="autocomplete-input"
                value={placeQuery}
                suggestions={placeList}
                completeMethod={fetchPlaceList}
                onChange={(e) => {
                  setPlaceQuery(e.value);
                  setSelectedPlace(undefined);
                }}
                onSelect={(e) => {
                  setSelectedPlace(e.value);
                }}
                itemTemplate={addressTemplate}
                placeholder="Veuillez sélectionner une ville"
              />
            </FormInput>

            <ImageForm
              image={mainImage}
              setImage={setMainImage}
              deleteImage={() => setMainImage(undefined)}
            />

            <FormInput
              label={`${isHighlight ? 'Le projet est mis en avant' : "Le projet n'est pas mis en avant"}`}>
              <InputSwitch checked={isHighlight} onChange={(e) => setIsHighlight(e.value)} />
            </FormInput>

            <div className={`flex flex-col gap-5 ${!isHighlight && 'opacity-30'} `}>
              <FormInput label={'Surface en m²'} required isValid={surface}>
                <InputNumber
                  id="surface"
                  value={surface}
                  placeholder="Veuillez saisir la surface"
                  onChange={(e) => setSurface(e.value)}
                  locale="fr"
                />
              </FormInput>

              <FormInput label={'Description'} translatable required isValid={description.length}>
                <RichTextEditor
                  value={description}
                  onChange={(value) => {
                    setDescription(value);
                  }}
                  readOnly={!isHighlight}
                />
              </FormInput>
              <FormInput label={'Avis Client'} translatable>
                <RichTextEditor
                  value={review}
                  onChange={(value) => {
                    setReview(value);
                  }}
                  readOnly={!isHighlight}
                />
              </FormInput>
            </div>
          </div>
          {footerButtons}
        </div>
      </LanguageManagerGuard>
    </Sidebar>
  );
}

export default ProjectPanel;
